import { ReactComponent as OutlineCam } from "assets/svg/outline-cam.svg";
import { ReactComponent as OutlineUserSpeaker } from "assets/svg/outline-user-speaker.svg";
import { ReactComponent as OutlineUsers } from "assets/svg/outline-users.svg";
import { OrchidProGroupSessionJoinButton } from "components/Dashboard/DoctorDashboard/components/orchid-pro-join-button";
import { useCalendarGroupSessions } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarGroupSessionsContext";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import SESSION_TYPE_COLORS from "components/Dashboard/utility/constants/session-type-colors";
import CalendarOverlayTrigger from "components/calendar-overlay-trigger/CalendarOverlayTrigger";
import ShowMore from "components/show-more";
import BodyEntry from "components/timeslot-popover/components/body-entry";
import SidesheetHeader from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/sidesheet-header/SidesheetHeader";
import { formatTimeslotPopoverSubtitle } from "components/timeslot-popover/utils/formatTimeslotPopoverSubtitle";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import moment from "moment-timezone";
import { useCallback, useMemo, useRef, useState } from "react";
import { Alert, Popover } from "react-bootstrap";
import cancelGroupSession from "services/scheduler/cancelGroupSession";
import canCancelSession from "utils/canCancelSession";
import canJoinSessionForPro from "utils/canJoinSessionForPro";
import getClasses from "utils/getClasses";
import { isGroupSessionCancelledByPro } from "utils/isGroupSessionCancelled";
import { useCalendarFreeze } from "../../../../contexts/calendar-freeze-context";
import { useCalendarTimeline } from "../../../../contexts/calendar-timeline-context/CalendarTimelineContextProvider";
import TimeslotButton from "../timeslot-button/TimeslotButton";
import TimeslotCancelSessionButton from "../timeslot-cancel-session-button/TimeslotCancelSessionButton";
import styles from "./GroupSessionTimeslot.module.scss";
import GroupSessionModal from "./components/group-session-modal/GroupSessionModal";
import { CalendarColumnPosition } from "../../utils/splitEventsIntoColumns";

const GroupSessionTimeslot = ({
  groupSession,
  date,
  calendarColumnPosition,
}: {
  groupSession: GroupSessionMetaData;
  calendarColumnPosition?: CalendarColumnPosition;
  date?: Date;
}) => {
  const { reloadGroupSessions } = useCalendarGroupSessions();
  const { currentCalendarTimezone } = useCalendarTime();
  const { nowValue } = useCalendarTimeline();
  const { open, close, show } = useToggleModal(false);
  const { registerFreezeId, unregisterFreezeId } = useCalendarFreeze();

  const { start, end } = useMemo(() => {
    const end = moment.tz(
      groupSession.group_session_end,
      currentCalendarTimezone,
    );
    const start = moment.tz(
      groupSession.group_session_start,
      currentCalendarTimezone,
    );
    return { start, end };
  }, [groupSession, currentCalendarTimezone]);

  const timeString = useMemo(
    () => formatTimeslotPopoverSubtitle(start, end, currentCalendarTimezone),
    [start, end, currentCalendarTimezone],
  );

  const active = useMemo(
    () => +start <= nowValue && nowValue <= +end,
    [start, nowValue, end],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<any>();
  const containerRef = useRef<HTMLButtonElement | null>(null);

  const handleClick = useCallback(async () => {
    containerRef.current?.focus();
    setBusy(true);
    try {
      await cancelGroupSession(groupSession.group_session_id);
      // if (onCancel) {
      //   onCancel(groupSession.group_session_id);
      // }
      if (reloadGroupSessions) {
        reloadGroupSessions();
      }
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  }, [groupSession.group_session_id, reloadGroupSessions]);

  const [{ canJoin, canCancel }, setPermissions] = useState({
    canJoin: false,
    canCancel: false,
  });

  const handleToggle = useCallback(
    (nextShow: boolean) => {
      if (nextShow) {
        setPermissions({
          canCancel: canCancelSession(end.valueOf()),
          canJoin: canJoinSessionForPro(start.valueOf(), end.valueOf()),
        });
      }
      // clear states when toggling
      setBusy(false);
      // last reset the error
      setError(undefined);
    },
    [end, start],
  );

  const isCancelled = useMemo(
    () => isGroupSessionCancelledByPro(groupSession),
    [groupSession],
  );

  const handleOpenModal = useCallback(
    (e) => {
      registerFreezeId(`group-session-timeslot-${start.valueOf()}`);
      if (e) {
        e.stopPropagation();
      }
      open();
    },
    [open, registerFreezeId, start],
  );

  const handleCloseModal = useCallback(
    (e) => {
      unregisterFreezeId(`group-session-timeslot-${start.valueOf()}`);
      if (e) {
        e.stopPropagation();
      }
      close();
    },
    [close, start, unregisterFreezeId],
  );

  const handleClosePopover = useCallback(
    () => containerRef.current?.blur(),
    [],
  );

  const openSeats = useMemo(
    () =>
      groupSession.patients_limit - groupSession.number_of_registered_patients,
    [groupSession.number_of_registered_patients, groupSession.patients_limit],
  );

  const SessionTitle = useMemo(
    () => (
      <>
        {/* session name  */}
        <h3 className={"popover-body-title"}>
          {[groupSession.series_title, groupSession.topic].join(" | ")}
        </h3>
        {/* session date time string */}
        <div
          className={"popover-body-time"}
        >{`${timeString} • $${groupSession.price.toFixed(2)}`}</div>
      </>
    ),
    [
      groupSession.price,
      groupSession.series_title,
      groupSession.topic,
      timeString,
    ],
  );

  const popover = useMemo(
    () => (
      <Popover
        id={`${groupSession.group_session_id}`}
        className={styles["group-session-timeslot__popover"]}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <SidesheetHeader
          title={messages.groupSession}
          onExpand={isCancelled ? undefined : handleOpenModal}
          onClose={handleClosePopover}
          className={getClasses(
            styles["group-session-timeslot__popover-title"],
            !isCancelled &&
              active &&
              styles["group-session-timeslot__popover-title--active"],
            isCancelled &&
              styles["group-session-timeslot__popover-title--cancelled"],
          )}
        />
        <Popover.Content>
          {/* error */}
          {error && <Alert variant="danger">{error.toString()}</Alert>}
          {SessionTitle}

          <div className={styles["group-session-timeslot__popover-list"]}>
            {/* speakers */}
            <BodyEntry
              compact
              Icon={<OutlineUserSpeaker />}
              title={
                (groupSession.speakers.length || "0") +
                " speaker" +
                (groupSession.speakers.length <= 1 ? "" : "s")
              }
            />

            {/* participants */}
            <BodyEntry
              compact
              Icon={<OutlineUsers />}
              title={
                (groupSession.number_of_registered_patients || "0") +
                " participant" +
                (groupSession.number_of_registered_patients <= 1 ? "" : "s")
              }
              subtitle={openSeats + " open seat" + (openSeats <= 1 ? "" : "s")}
            />

            {/* join on zoom */}
            <BodyEntry
              compact
              Icon={<OutlineCam />}
              Action={
                <OrchidProGroupSessionJoinButton
                  groupSession={groupSession}
                  variant={"link"}
                  className={styles["group-session-timeslot__popover-join-btn"]}
                  disabled={busy}
                  title={messages.joinOnZoom}
                />
              }
            />
          </div>

          {groupSession.description && (
            <div className={"popover-note"}>
              <div
                className={
                  styles["group-session-timeslot__popover-description-label"]
                }
              >
                {messages.sessionDescription}
              </div>
              <ShowMore
                content={groupSession.description}
                lines={2}
                onClick={() => containerRef.current?.focus()}
              />
            </div>
          )}

          {isCancelled ? (
            <div className={"popover-footer"}>
              <div className={"popover-footer-note"}>
                {messages.iCancelledTheSession}
              </div>
            </div>
          ) : (
            (canJoin || canCancel) && (
              <div className={"popover-footer"}>
                {canCancel && (
                  <TimeslotCancelSessionButton
                    className={"popover-footer-secondary-btn"}
                    busy={busy}
                    disabled={busy}
                    onClick={handleClick}
                  />
                )}
                {canJoin && (
                  <OrchidProGroupSessionJoinButton
                    groupSession={groupSession}
                    className={"popover-footer-primary-btn"}
                    disabled={busy}
                    title={messages.joinNow}
                  />
                )}
              </div>
            )
          )}
        </Popover.Content>
      </Popover>
    ),
    [
      SessionTitle,
      active,
      busy,
      canCancel,
      canJoin,
      error,
      groupSession,
      handleClick,
      handleClosePopover,
      handleOpenModal,
      isCancelled,
      openSeats,
    ],
  );

  const typeIndicatorColor = useMemo(() => {
    if (isCancelled) {
      return "#fb545e";
    }

    if (active) {
      return "#15a612";
    }

    return "#c0ea4a";
  }, [isCancelled, active]);

  return (
    <>
      {/* modal */}
      <GroupSessionModal
        Header={
          <SidesheetHeader
            title={messages.groupSession}
            onClose={handleCloseModal}
            className={
              styles["group-session-timeslot__popover-title"] +
              " " +
              styles["group-session-timeslot__popover-title--modal"]
            }
          />
        }
        show={show}
        onHide={handleCloseModal}
        groupSession={groupSession}
        Title={[groupSession.series_title, groupSession.topic].join(" | ")}
        Subtitle={timeString}
        SessionHeader={
          <>
            <div className={styles["group-session-timeslot__session-details"]}>
              {SessionTitle}
            </div>
            <div className={styles["group-session-timeslot__body-entry-list"]}>
              {/* speakers */}
              <BodyEntry
                compact
                Icon={<OutlineUserSpeaker />}
                title={
                  (groupSession.speakers.length || "0") +
                  " speaker" +
                  (groupSession.speakers.length <= 1 ? "" : "s")
                }
              />

              {/* participants */}
              <BodyEntry
                compact
                Icon={<OutlineUsers />}
                title={
                  (groupSession.number_of_registered_patients || "0") +
                  " participant" +
                  (groupSession.number_of_registered_patients <= 1 ? "" : "s")
                }
                subtitle={
                  openSeats + " open seat" + (openSeats <= 1 ? "" : "s")
                }
              />

              {/* join on zoom */}
              <BodyEntry
                compact
                Icon={<OutlineCam />}
                Action={
                  <OrchidProGroupSessionJoinButton
                    groupSession={groupSession}
                    variant={"link"}
                    className={
                      styles["group-session-timeslot__popover-join-btn"]
                    }
                    disabled={busy}
                    title={messages.joinOnZoom}
                  />
                }
              />
            </div>
          </>
        }
        Footer={
          canCancel && (
            <TimeslotCancelSessionButton
              inModal
              busy={busy}
              disabled={busy}
              onClick={handleClick}
            />
          )
        }
      />
      {/* button */}

      <CalendarOverlayTrigger overlay={popover} onToggle={handleToggle}>
        <TimeslotButton
          ref={containerRef}
          className={styles["group-session-timeslot"]}
          style={{
            backgroundColor: SESSION_TYPE_COLORS[0].value,
          }}
          startDate={groupSession.group_session_start}
          endDate={groupSession.group_session_end}
          typeIndicatorColor={typeIndicatorColor}
          timeslotName={groupSession.topic}
          isCancelled={isCancelled}
          date={date}
          calendarColumnPosition={calendarColumnPosition}
        />
      </CalendarOverlayTrigger>
    </>
  );
};

export default GroupSessionTimeslot;
